import { useEffect, useState, useMemo, useRef } from "react";
import type { Dispatch, SetStateAction } from "react";
import { useNavigate } from "react-router";

import { TabView, TabPanel } from "primereact/tabview";
import { InputText } from "primereact/inputtext";
import { ScrollPanel } from "primereact/scrollpanel";
import { DataView } from "primereact/dataview";
import { Button } from "primereact/button";
import { Skeleton } from "primereact/skeleton";

import { useBreadcrumb } from "sj-ui-toolkit/src/atlas/hooks/useBreadcrumbs";
import { ContentCardSkeleton } from "sj-ui-toolkit/src/atlas/components/ContentCard";
import { PathCard } from "sj-ui-toolkit/src/atlas/components/ContentCard/PathCard/PathCard";
import type { VariantType } from "sj-ui-toolkit/src/atlas/components/ContentCard/contentCardTypes";
import { useToast } from "sj-ui-toolkit/src/hooks";
import { AdminAppRoutes } from "sj-ui-toolkit/src/constants/routes";

import { loadFilteredPathsAsync, getPathType, toggleActivated, type PathData } from "@/api/paths";
import { truncate } from "lodash";

const NO_QUESTS_FOUND = "No quests found";

const getVariantFromType = (_type?: string): VariantType => "teal";

interface QuestsState {
  quests: PathData[];
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

const Quests = () => {
  useBreadcrumb({
    label: "Quests",
    route: AdminAppRoutes.routes.paths.routes.quests.route,
    display: "none",
    resetTrail: true,
  });

  const navigate = useNavigate();
  const { showToast } = useToast();

  const [data, setData] = useState<QuestsState>({ quests: [] });
  const [isLoading, setIsLoading] = useState(true);
  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [isLoadingToggle, setIsLoadingToggle] = useState(false);

  const isMountedRef = useRef(false);

  useEffect(() => {
    if (isMountedRef.current) return;
    isMountedRef.current = true;
    void fetchData();
  }, []);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const response = await loadFilteredPathsAsync({ path_type: "quest", path_source: "custom" });
      setData({ quests: response.paths ?? [] });
    } catch {
      showToast({
        severity: "error",
        summary: "Error",
        detail: "Failed to load quests",
        life: 3000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const filterData = (items: PathData[]) => {
    return items.filter(item => {
      return (
        !searchTerm ||
        item.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.description?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    });
  };

  const handleToggleActivated = async (item: PathData) => {
    setIsLoadingToggle(true);
    await toggleActivated({
      id: item.id!,
      paths: data.quests,
      type: null,
      setData: setData as Dispatch<
        SetStateAction<{ customPaths?: PathData[]; defaultPaths?: PathData[]; quests?: PathData[] }>
      >,
      showToast,
    });
    setIsLoadingToggle(false);
  };

  const filteredData = useMemo(
    () => ({
      active: filterData(data.quests.filter(item => item.activated && !item.archived)),
      inactive: filterData(data.quests.filter(item => !(item.activated && !item.archived))),
    }),
    [data, searchTerm]
  );

  return (
    <div className="relative w-full">
      <div className="mb-4 flex items-center justify-between">
        <h1>Quests</h1>
        <Button
          label="Create New Quest"
          icon="pi pi-hammer"
          onClick={() => navigate(`${AdminAppRoutes.routes.paths.routes.new.route}?type=quest`)}
        />
      </div>

      <div className="py-8">
        <div className="flex flex-wrap gap-4 items-center mb-4">
          <InputText
            value={searchTerm}
            disabled={isLoading}
            onChange={e => setSearchTerm(e.target.value)}
            placeholder="Search Quests"
            aria-label="Search quests"
          />
        </div>
      </div>

      <TabView
        aria-label="Quests"
        activeIndex={activeTabIndex}
        onTabChange={e => setActiveTabIndex(e.index)}
      >
        <TabPanel header="Active">
          <ScrollPanel
            style={{ width: "100%", height: "70vh" }}
            pt={{ barY: { style: { backgroundColor: "var(--primary-color)" } } }}
          >
            <QuestsTabContent
              data={filteredData.active}
              isLoading={isLoading}
              toggleActivated={handleToggleActivated}
              isLoadingToggle={isLoadingToggle}
            />
          </ScrollPanel>
        </TabPanel>
        <TabPanel header="Inactive">
          <ScrollPanel
            style={{ width: "100%", height: "70vh" }}
            pt={{ barY: { style: { backgroundColor: "var(--primary-color)" } } }}
          >
            <QuestsTabContent
              data={filteredData.inactive}
              isLoading={isLoading}
              toggleActivated={handleToggleActivated}
              isLoadingToggle={isLoadingToggle}
            />
          </ScrollPanel>
        </TabPanel>
      </TabView>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Tab content sub-component
// ---------------------------------------------------------------------------

interface QuestsTabContentProps {
  data: PathData[];
  isLoading: boolean;
  toggleActivated: (item: PathData) => void;
  isLoadingToggle: boolean;
}

const QuestsTabContent = ({
  data,
  isLoading,
  toggleActivated,
  isLoadingToggle,
}: QuestsTabContentProps) => {
  if (isLoading) {
    return (
      <div className="space-y-8">
        <div className="mb-4">
          <Skeleton width="10rem" height="1rem" />
        </div>
        <div className="flex flex-wrap gap-6">
          {Array(8)
            .fill(null)
            .map((_, idx) => (
              <ContentCardSkeleton key={idx} cardSize="small" showActivated />
            ))}
        </div>
      </div>
    );
  }

  const listTemplate = (items: PathData[]) => {
    const sortedItems = [...items].sort((a, b) => a.name.localeCompare(b.name));

    return (
      <div className="flex flex-wrap gap-6">
        {sortedItems.map(item => (
          <PathCard
            key={item.id}
            contentType="path"
            lessonType={undefined}
            pathType={getPathType(item)}
            id={String(item.id ?? "")}
            name={truncate(item.name, { length: 50 })}
            description={truncate(item.description, { length: 120 })}
            lessonsInEnrollment={item.lessonsCount}
            estimatedTimeToComplete={item.totalDuration}
            points={item.totalPoints ?? 0}
            variant={getVariantFromType(item.contentType) as VariantType}
            cardSize="small"
            showActivated
            activated={item.activated}
            onActivate={() => toggleActivated(item)}
            isLoadingToggle={isLoadingToggle}
            restricted={item.restricted}
          />
        ))}
      </div>
    );
  };

  return (
    <DataView
      value={data}
      listTemplate={listTemplate}
      layout="list"
      emptyMessage={NO_QUESTS_FOUND}
    />
  );
};

export default Quests;
